/**
 * Brain client API — Phase 11.
 *
 * Thin wrappers around the brain-* Netlify functions. The SSE chat endpoint
 * exposes `chatStream()` which yields parsed events as they arrive — keep the
 * UI in lockstep with the server.
 */
import { getSupabase } from './supabase';

export interface BrainThread {
  id: string;
  title: string | null;
  pinned: boolean;
  created_at: string;
  updated_at: string;
}

export interface BrainStoredMessage {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  tool_calls: Array<{ id: string; name: string; input: Record<string, unknown> }> | null;
  tool_results: Array<{ tool_use_id: string; content: string; is_error?: boolean }> | null;
  model: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  cache_read_tokens: number | null;
  created_at: string;
}

export type BrainStreamEvent =
  | { type: 'thread'; id: string }
  | { type: 'tool_use'; name: string; input: Record<string, unknown> }
  | { type: 'tool_result'; name: string; ok: boolean; ms: number; error?: string }
  | { type: 'delta'; text: string }
  | { type: 'done'; usage: { input_tokens: number; output_tokens: number; cache_read_input_tokens?: number; cache_creation_input_tokens?: number }; model: string }
  | { type: 'error'; message: string };

async function authHeader(): Promise<Record<string, string>> {
  const { data } = await getSupabase().auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

export async function listThreads(): Promise<BrainThread[]> {
  const res = await fetch('/.netlify/functions/brain-list-threads', {
    headers: { ...(await authHeader()) },
  });
  if (!res.ok) throw new Error(`listThreads failed: ${res.status}`);
  const body = await res.json() as { threads: BrainThread[] };
  return body.threads;
}

export async function getThread(id: string): Promise<{ thread: BrainThread; messages: BrainStoredMessage[] }> {
  const res = await fetch(`/.netlify/functions/brain-get-thread?id=${encodeURIComponent(id)}`, {
    headers: { ...(await authHeader()) },
  });
  if (!res.ok) throw new Error(`getThread failed: ${res.status}`);
  return await res.json();
}

export async function deleteThread(id: string): Promise<void> {
  const res = await fetch('/.netlify/functions/brain-delete-thread', {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify({ id }),
  });
  if (!res.ok) throw new Error(`deleteThread failed: ${res.status}`);
}

export interface ChatStreamOpts {
  thread_id?: string;
  message: string;
  model?: 'claude-sonnet-4-6' | 'claude-haiku-4-5-20251001';
  signal?: AbortSignal;
  onEvent: (event: BrainStreamEvent) => void;
}

/** Streams chat events from /brain-chat. Returns when the stream closes. */
export async function chatStream(opts: ChatStreamOpts): Promise<void> {
  const res = await fetch('/.netlify/functions/brain-chat', {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify({
      thread_id: opts.thread_id,
      message: opts.message,
      model: opts.model,
    }),
    signal: opts.signal,
  });

  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => '');
    throw new Error(`brain-chat ${res.status}: ${text.slice(0, 200)}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buffer.indexOf('\n\n')) !== -1) {
      const chunk = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const line = chunk.split('\n').find((l) => l.startsWith('data: '));
      if (!line) continue;
      const payload = line.slice(6);
      try {
        const event = JSON.parse(payload) as BrainStreamEvent;
        opts.onEvent(event);
      } catch {
        // ignore non-JSON keepalives
      }
    }
  }
}
